import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaMapMarkerAlt, FaSearch, FaTimes, FaLink } from 'react-icons/fa';
import { isStoreOpen } from '../../utils/storeTimeUtils';
import { searchProducts, clearProducts, fetchProducts } from '../../redux/menu/ProductSlice';
import { useNetworkError } from '../../hooks/useNetworkError';
import NetworkError from '../NetworkError/NetworkError';

const Navbar = () => {
    const [changeHeader, setChangeHeader] = useState(false)
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const [searchText, setSearchText] = useState('')
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { profile } = useSelector((state) => state.profile);
    const { categories } = useSelector(state => state.category);
    const { status, isSearchMode, error: searchError } = useSelector(state => state.product);
    const { showNetworkError, errorMessage, handleNetworkError, retryAction } = useNetworkError();
    
    // Check if we're on the home screen
    const isHomeScreen = location.pathname === '/';
    
    // Check if store is currently open
    const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
    
    // Check if store status is available (not false)
    const storeStatusAvailable = profile?.status !== false;
    
    // Store is available for orders if it's open and status is available
    const storeAvailable = storeIsOpen && storeStatusAvailable;

    //header change function 
    const onChangeHeader = () => {
        if (window.scrollY >= 50) {
            setChangeHeader(true)
        } else {
            setChangeHeader(false)
        }
    }

    //change header by scrolling
    window.addEventListener('scroll', onChangeHeader)

    const handleMap = () => {
        window.open(profile?.map_url , "_blank");
    };

    const handleConnect = () => {
        navigate('/connect');
    };

    // Search functionality
    const handleSearch = () => { 
        if (searchText.trim()) {
            // Get the first category ID or use a default one
            const categoryId = categories && categories.length > 0 ? categories[0].id : 'default'
            
            // Clear previous products and search for new ones
            dispatch(clearProducts())
            
            // Dispatch search action
            dispatch(searchProducts({ 
                categoryID: categoryId, 
                text: searchText.trim() 
            })).then((result) => {
               
            })
        } else {
            // If search text is empty, return to initial state
            handleClearSearch()
        }
    }

    const handleClearSearch = useCallback(() => {
        // Clear search mode and load initial products
        if (categories && categories.length > 0) {
            dispatch(clearProducts())
            dispatch(fetchProducts({ 
                categoryID: categories[0].id, 
                page: 1 
            }))
        }
        setSearchText('')
        setIsSearchExpanded(false)
    }, [categories, dispatch])

    // Handle when search text is cleared
    useEffect(() => {
        if (searchText === '' && isSearchMode) {
            handleClearSearch()
        }
    }, [searchText, isSearchMode, handleClearSearch])

    // Handle network errors from search
    useEffect(() => {
        if (status === 'failed' && searchError?.type === 'NETWORK_ERROR') {
            handleNetworkError(searchError, searchError.message);
        }
    }, [status, searchError, handleNetworkError]);

    // Retry function for search network errors
    const handleSearchRetry = () => {
        retryAction(() => {
            if (searchText.trim()) {
                const categoryId = categories && categories.length > 0 ? categories[0].id : 'default'
                dispatch(clearProducts())
                dispatch(searchProducts({ 
                    categoryID: categoryId, 
                    text: searchText.trim() 
                }))
            }
        });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const toggleSearch = () => {
        setIsSearchExpanded(!isSearchExpanded)
        if (isSearchExpanded) {
            handleClearSearch()
        }
    }

    return (
        <>
        <header className={changeHeader ? "fixed z-50 top-0 left-0 w-full shadow-md transition duration-500" : "bg-transparent fixed z-50 top-0 left-0 w-full transition duration-500"} style={{ backgroundColor: changeHeader ? '#fcf4e0' : 'transparent' }}>
            <nav className="flex items-center max-w-screen-xl mx-auto px-3 sm:px-4 md:px-6 py-2 md:py-3">
                {/* left  */}
                <div className="flex flex-grow">
                    <img 
                        className="w-16 h-14 sm:w-18 sm:h-16 md:w-20 md:h-16 lg:w-28 lg:h-24 cursor-pointer object-contain" 
                        src={profile?.DocLogo?.imageUrl } 
                        alt={profile?.name || "logo"} 
                        onClick={() => navigate('/')} 
                    />
                </div>
                {/* right  */}
                {storeAvailable && (
                    <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
                        {/* Search Button/Input - Only show on home screen */}
                        {isHomeScreen && (
                            <div className="relative">
                                {!isSearchExpanded ? (
                                    <button 
                                        onClick={toggleSearch}
                                        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:bg-white"
                                        title="Search"
                                    >
                                        <FaSearch className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                                        <span className="hidden sm:block md:block poppins font-medium text-xs sm:text-sm text-primary">Search</span>
                                    </button>
                                ) : (
                                    <div className="flex items-center space-x-1 sm:space-x-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5">
                                        <input 
                                            type="text" 
                                            className="outline-none bg-transparent text-xs sm:text-sm w-20 sm:w-24 md:w-32 lg:w-48 text-gray-700 placeholder-gray-400" 
                                            placeholder="Search..." 
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            autoFocus
                                        />
                                        <button 
                                            onClick={handleSearch}
                                            className={`text-xs py-0.5 sm:py-1 px-2 sm:px-3 rounded-full text-white poppins transition duration-300 ${
                                                status === 'loading' ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                                            }`}
                                            disabled={status === 'loading'}
                                        >
                                            {status === 'loading' ? '...' : 'Go'}
                                        </button>
                                        <button 
                                            onClick={toggleSearch}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                            title="Close search"
                                        >
                                            <FaTimes className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <button 
                            onClick={handleMap}
                            className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:bg-white"
                            title="View on Map"
                        >
                            <FaMapMarkerAlt className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                            <span className="hidden lg:block poppins font-medium text-sm text-blue-600">Map</span>
                        </button>
                        <button 
                            onClick={handleConnect}
                            className="hidden md:flex items-center space-x-2 px-3 md:px-4 py-1.5 md:py-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:bg-white"
                            title="Connect with us"
                        >
                            <FaLink className="w-3 h-3 md:w-4 md:h-4 text-purple-600" />
                            <span className="hidden lg:block poppins font-medium text-sm text-purple-600">Connect</span>
                        </button>
                    </div>
                )}

            </nav>
        </header>
        
        {/* Network Error Modal */}
        {showNetworkError && (
            <NetworkError 
                message={errorMessage}
                onRetry={handleSearchRetry}
            />
        )}
    </>
    )
}

export default Navbar
